import React, { createContext, useContext, useState } from 'react';
import AlertComponent from '../components/common/AlertComponent';

const AlertContext = createContext();

/**
 * Provider component for managing alerts
 */
export const AlertProvider = ({ children }) => {
  const [alerts, setAlerts] = useState([]);
  const [alertId, setAlertId] = useState(0);

  /**
   * Show an alert
   * @param {string} message - Alert message
   * @param {string} type - Alert type (success, error, warning, info)
   * @param {string} [title] - Alert title
   * @param {number} [duration] - Duration in ms
   */
  const showAlert = (message, type, title, duration) => {
    const id = alertId;
    setAlertId(prevId => prevId + 1);
    
    setAlerts(prev => [...prev, { id, message, type, title, duration }]);
    
    // Auto-remove the alert after it closes to prevent memory leaks
    if (duration) {
      setTimeout(() => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
      }, duration + 500); // Add a small buffer to ensure animation completes
    }
    
    return id;
  };

  /**
   * Close a specific alert by ID
   * @param {number} id - Alert ID to close
   */
  const closeAlert = (id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  };

  return (
    <AlertContext.Provider value={{ showAlert, closeAlert }}>
      {children}
      <div style={{ 
        position: 'fixed', 
        top: 20, 
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        gap: 10
      }}>
        {alerts.map(alert => (
          <AlertComponent
            key={alert.id}
            message={alert.message}
            type={alert.type}
            title={alert.title}
            open={true}
            onClose={() => closeAlert(alert.id)}
            duration={alert.duration || 6000}
            autoHide={!!alert.duration}
          />
        ))}
      </div>
    </AlertContext.Provider>
  );
};

/**
 * Hook to use the alert context
 * @returns {Object} Alert context methods
 */
export const useAlert = () => {
  const context = useContext(AlertContext);
  
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  
  return {
    /**
     * Show a success alert
     * @param {string} message - Alert message
     * @param {string} [title] - Alert title
     * @param {number} [duration] - Duration in ms
     */
    success: (message, title = 'Success', duration = 6000) => {
      return context.showAlert(message, 'success', title, duration);
    },
    
    /**
     * Show an error alert
     * @param {string} message - Alert message
     * @param {string} [title] - Alert title
     * @param {number} [duration] - Duration in ms
     */
    error: (message, title = 'Error', duration = 8000) => {
      return context.showAlert(message, 'error', title, duration);
    },
    
    /**
     * Show a warning alert
     * @param {string} message - Alert message
     * @param {string} [title] - Alert title
     * @param {number} [duration] - Duration in ms
     */
    warning: (message, title = 'Warning', duration = 7000) => {
      return context.showAlert(message, 'warning', title, duration);
    },
    
    /**
     * Show an info alert
     * @param {string} message - Alert message
     * @param {string} [title] - Alert title
     * @param {number} [duration] - Duration in ms
     */
    info: (message, title = 'Info', duration = 5000) => {
      return context.showAlert(message, 'info', title, duration);
    },
    
    /**
     * Close an alert
     * @param {number} id - Alert ID to close
     */
    close: (id) => {
      context.closeAlert(id);
    }
  };
};